import {
	Flex,
	FormControlText,
	Input,
	InputCheckBox,
	TextLink
} from "@itandi/itandi-bb-ui";
import { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { usePropertySelectHandler } from "~/hooks/deals/useRoomBlockPropertySelect";
import { useDebounce } from "~/hooks/useDebounce";
import { usePropertyBuildingOptions } from "~/hooks/usePropertyBuildingOptions";
import { usePropertyRoomOptions } from "~/hooks/usePropertyRoomOptions";
import type { DealParam, DealUpdateParam } from "~/models/DealForm";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import { buildPropertyByLeasing } from "~/models/kensaku_kun/property";
import type { Leasing } from "~/models/Leasing";
import type { PropertyBuildingOption } from "~/models/PropertyBuildingOption";
import {
	buildingOptions,
	buildPropertyBuildingOptionsFromLeasing,
	buildPropertyBuildingOptionsFromRoomName,
	propertyBuildingOptionValue
} from "~/models/PropertyBuildingOption";
import type { PropertyRoomOption } from "~/models/PropertyRoomOption";
import {
	buildPropertyRoomOptionsFromRoomNumber,
	propertyRoomOptionValue,
	roomOptions
} from "~/models/PropertyRoomOption";
import {
	editRentParkingSpaceUrl,
	editRentRoomBuildingUrl,
	editRentRoomUrl
} from "~/utils/BukkenKanriPath";
import { FormRow } from "./FormRow";
import Styles from "./RoomBlockPropertySelectV2.module.scss";

type Props = {
	leasing: Leasing | null;
	selectedRoom: PropertyRoomOption | null;
	setSelectedRoom: (property: PropertyRoomOption | null) => void;
};

export const RoomBlockPropertySelectV2: React.FC<Props> = ({
	leasing,
	selectedRoom,
	setSelectedRoom
}) => {
	const { company } = useContext(ItandiAccountContext);
	// KTの契約にBKK物件が紐づいていないことが多い。そのため、KT経由の案件作成時はBKK以外の物件も保存できるようにする
	const showOnlyAmaterasProperties =
		!!company?.onlyAmaterasProperties && !leasing;
	const {
		register,
		control,
		formState: { errors },
		setValue
	} = useFormContext<DealParam | DealUpdateParam>();
	const isRegisterAsBuilding = useWatch({
		control,
		name: "isRegisterAsBuilding"
	});
	const debounce = useDebounce(200);
	const [inputRoomName, setInputRoomName] = useState<string>("");
	const [inputRoomNumber, setInputRoomNumber] = useState<string>("");
	const [selectedBuilding, setSelectedBuilding] =
		React.useState<PropertyBuildingOption | null>(null);
	const handleInputChangeBuilding = useCallback(
		(text: string) => {
			debounce(() => {
				setInputRoomName(text.trim());
			});
		},
		[debounce, setInputRoomName]
	);
	const handleInputChangeRoom = useCallback(
		(text: string) => {
			setInputRoomNumber(text.trim());
		},
		[setInputRoomNumber]
	);
	// フォームに表示する物件名と部屋番号の候補を取得
	const {
		data: propertyBuildingOptionsFromApi,
		isValidating: isPropertyBuildingOptionsValidating
	} = usePropertyBuildingOptions().index(inputRoomName);
	const {
		data: propertyRoomOptionsFromApi,
		isValidating: isPropertyRoomOptionsValidating
	} = usePropertyRoomOptions().index(
		selectedBuilding?.name ?? null,
		selectedBuilding?.address ?? null,
		selectedBuilding?.amaterasPropertyId ?? null
	);

	const propertyBuildingOptions = useMemo(() => {
		const roomNameOptions = showOnlyAmaterasProperties
			? []
			: buildPropertyBuildingOptionsFromRoomName(inputRoomName);

		return [...roomNameOptions, ...(propertyBuildingOptionsFromApi ?? [])];
	}, [
		showOnlyAmaterasProperties,
		inputRoomName,
		propertyBuildingOptionsFromApi
	]);
	const propertyRoomOptions = useMemo(() => {
		const roomNumberOptions = showOnlyAmaterasProperties
			? []
			: buildPropertyRoomOptionsFromRoomNumber(inputRoomNumber);

		return [...roomNumberOptions, ...(propertyRoomOptionsFromApi ?? [])];
	}, [showOnlyAmaterasProperties, inputRoomNumber, propertyRoomOptionsFromApi]);
	// BKKのみフラグがONで部屋が登録されていないBKK物件を選択した時、自動で棟または戸建てとして登録にチェックを入れる
	const shouldDisableRegisterAsBuilding =
		showOnlyAmaterasProperties &&
		!!selectedBuilding &&
		!isPropertyRoomOptionsValidating &&
		propertyRoomOptions.length === 0;
	const shouldDisableAddress =
		!!selectedBuilding?.amaterasPropertyId && !!selectedBuilding.address;
	// KTで駐車場区分が紐づいている契約でNSKの案件作成に訪れたとき、BKK駐車場区画へのリンクを出す対応
	const showParkingSpaceProductLink =
		leasing?.amaterasPropertyType === "parking_space";

	const {
		onChangeBuilding,
		onChangeRoom,
		onChangeRegisterAsBuildingCheck,
		onBlurBuilding,
		onBlurRoom
	} = usePropertySelectHandler({
		propertyBuildingOptions,
		propertyRoomOptions,
		selectedBuilding,
		selectedRoom,
		setSelectedBuilding,
		setSelectedRoom,
		setValue
	});

	useEffect(() => {
		if (!leasing) return;
		setValue("roomName", leasing.roomName);
		setValue("roomNumber", leasing.roomNumber);
		setValue("address", leasing.address);
		setValue(
			"amaterasBuildingPropertyId",
			leasing.amaterasBuildingPropertyId?.toString() ?? null
		);
		const property = buildPropertyByLeasing(leasing);
		setValue("property", property);

		setSelectedBuilding(buildPropertyBuildingOptionsFromLeasing(leasing));
	}, [leasing, setValue]);

	useEffect(() => {
		if (!isRegisterAsBuilding && shouldDisableRegisterAsBuilding) {
			onChangeRegisterAsBuildingCheck(true);
			toast.success(
				"部屋の登録がない物件のため、「棟または戸建てとして登録」にチェックを入れました。"
			);
		}
	}, [
		isRegisterAsBuilding,
		shouldDisableRegisterAsBuilding,
		onChangeRegisterAsBuildingCheck
	]);

	return (
		<>
			<FormRow error={errors.roomName?.message} label="物件名" required>
				<Flex alignItems="center" direction="row" gap=".5rem">
					<div className={Styles.ComboBoxContainer}>
						<SingleComboBox
							isInvalid={errors.roomName != null}
							loading={isPropertyBuildingOptionsValidating}
							onBlur={onBlurBuilding}
							onChange={onChangeBuilding}
							onInputChange={handleInputChangeBuilding}
							options={buildingOptions(propertyBuildingOptions)}
							placeholder="例）サンプルマンション"
							ref={register("roomName").ref}
							value={propertyBuildingOptionValue(selectedBuilding)}
							withAsyncOptions
						/>
					</div>
					{selectedBuilding?.amaterasPropertyId ? (
						<div className={Styles.ProductLinkContainer}>
							<TextLink
								color="link"
								href={editRentRoomBuildingUrl(
									selectedBuilding.amaterasPropertyId
								)}
								target="_blank"
							>
								物件システム
							</TextLink>
						</div>
					) : null}
				</Flex>
				<FormControlText help="2文字以上入力すると候補が最大100件まで表示されます。" />
				<FormControlText help="「・」などの記号を含む物件名がヒットしない場合は、記号を省いたフリガナで検索をお試しください。" />
			</FormRow>
			<FormRow error={errors.roomNumber?.message} label="部屋番号" required>
				<Flex direction="column" gap=".5rem">
					<InputCheckBox
						checked={isRegisterAsBuilding}
						disabled={shouldDisableRegisterAsBuilding}
						label="棟または戸建てとして登録"
						onChange={(event): void => {
							const { checked } = event.target;
							onChangeRegisterAsBuildingCheck(checked);
						}}
					/>
					<Flex alignItems="center" direction="row" gap=".5rem">
						<div className={Styles.ComboBoxContainer}>
							<SingleComboBox
								isDisabled={isRegisterAsBuilding}
								isInvalid={errors.roomNumber != null}
								loading={isPropertyRoomOptionsValidating}
								onBlur={onBlurRoom}
								onChange={onChangeRoom}
								onInputChange={handleInputChangeRoom}
								options={roomOptions(propertyRoomOptions)}
								placeholder="例）101"
								ref={register("roomNumber").ref}
								value={propertyRoomOptionValue(selectedRoom)}
								withAsyncOptions
							/>
						</div>
						{selectedRoom?.amaterasPropertyId ? (
							<div className={Styles.ProductLinkContainer}>
								<TextLink
									color="link"
									href={
										showParkingSpaceProductLink
											? editRentParkingSpaceUrl(selectedRoom.amaterasPropertyId)
											: editRentRoomUrl(selectedRoom.amaterasPropertyId)
									}
									target="_blank"
								>
									物件システム
								</TextLink>
							</div>
						) : null}
					</Flex>
				</Flex>
			</FormRow>

			<FormRow error={errors.address?.message} label="物件住所" required>
				<Input
					disabled={shouldDisableAddress}
					isInvalid={errors.address != null}
					placeholder="例）東京都港区六本木1-2-3"
					{...register("address")}
				/>
			</FormRow>
		</>
	);
};
